<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";

import { ref, computed, onMounted } from "vue";
import moment from "moment";
import DateRangePicker from "daterangepicker";

import { useExpenseStore } from "@/stores/expenseStore";
import ExpensesReportTable from "./expenses-report-table.vue";

const title = "Expense Report";
const text = "Finance & Accounts";
const text1 = "Expense Report";

const expenseStore = useExpenseStore();

const dateRangeInput = ref(null);
const sortMode = ref("last_7_days");
const selectedYear = ref(moment().year());

const changePercents = ref({
  total: null,
  approved: null,
  pending: null,
  paid: null,
});

const amountRanges = [
  { label: "All", min: null, max: null },
  { label: "0 - 1,000", min: 0, max: 1000 },
  { label: "1,000 - 5,000", min: 1000, max: 5000 },
  { label: "5,000 - 10,000", min: 5000, max: 10000 },
  { label: "10,000+", min: 10000, max: null },
];

const amountLabel = computed(() => {
  const { min_amount, max_amount } = expenseStore.filters;
  const found = amountRanges.find(
    (r) =>
      (r.min ?? null) === (min_amount ?? null) &&
      (r.max ?? null) === (max_amount ?? null)
  );
  return found ? found.label : "Custom amount";
});

// Build payment method list from loaded data
const paymentMethods = computed(() => {
  const set = new Set();
  (expenseStore.items || []).forEach((e) => {
    if (e.payment_method) set.add(e.payment_method);
  });
  return ["All", ...Array.from(set)];
});

const paymentLabel = computed(
  () => expenseStore.filters.payment_method || "All"
);

const stats = computed(() => expenseStore.stats);
const loading = computed(() => expenseStore.loading);
const rawItems = computed(() => expenseStore.items || []);

const sortLabel = computed(() => {
  switch (sortMode.value) {
    case "recent":
      return "Recently Added";
    case "amount_asc":
      return "Amount (Low → High)";
    case "amount_desc":
      return "Amount (High → Low)";
    case "last_month":
      return "Last Month";
    case "last_7_days":
      return "Last 7 Days";
    default:
      return "Custom";
  }
});

const sortedData = computed(() => {
  const list = [...rawItems.value];
  switch (sortMode.value) {
    case "amount_asc":
      return list.sort((a, b) => a.amount - b.amount);
    case "amount_desc":
      return list.sort((a, b) => b.amount - a.amount);
    case "recent":
    case "last_month":
    case "last_7_days":
    default:
      return list.sort(
        (a, b) =>
          new Date(b.expense_date || b.created_at || 0) -
          new Date(a.expense_date || a.created_at || 0)
      );
  }
});

/** ---- Chart (Apex) based on real expenses ---- */
const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const expenseChartSeries = computed(() => {
  const monthly = Array(12).fill(0);

  rawItems.value.forEach((e) => {
    if (!e.expense_date) return;
    const mDate = moment(e.expense_date, "YYYY-MM-DD", true);
    if (!mDate.isValid()) return;
    if (mDate.year() !== selectedYear.value) return;
    const idx = mDate.month(); // 0-11
    monthly[idx] += Number(e.amount || 0);
  });

  // Convert to 'K' units for the chart
  const dataInK = monthly.map((v) => Number((v / 1000).toFixed(2)));

  return [
    {
      name: "Total Expense",
      data: dataInK,
    },
  ];
});

const expenseChartOptions = computed(() => {
  const series = expenseChartSeries.value[0]?.data || [];
  const maxVal = Math.max(0, ...series);
  const yMax = maxVal === 0 ? 10 : Math.ceil(maxVal / 10) * 10;

  return {
    chart: {
      height: 190,
      type: "area",
      zoom: { enabled: false },
    },
    colors: ["#FF9F43"],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth" },
    xaxis: {
      categories: months,
    },
    yaxis: {
      min: 0,
      max: yMax,
      tickAmount: 5,
      labels: {
        offsetX: -15,
        formatter: (val) => `${val}K`,
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "left",
    },
  };
});

/** ---- Helpers ---- */

function fmtAmount(v) {
  return Number(v || 0).toLocaleString();
}

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function setAmountRange(range) {
  expenseStore.filters.min_amount = range.min;
  expenseStore.filters.max_amount = range.max;
  expenseStore.filters.page = 1;
  reload();
}

function setPaymentMethod(label) {
  expenseStore.filters.payment_method = label === "All" ? "" : label;
  expenseStore.filters.page = 1;
  reload();
}

function setYear(year) {
  selectedYear.value = year;

  // Apply full-year range to filters + picker
  const start = moment(`${year}-01-01`);
  const end = moment(`${year}-12-31`);

  expenseStore.filters.date_from = start.format("YYYY-MM-DD");
  expenseStore.filters.date_to = end.format("YYYY-MM-DD");
  if (dateRangeInput.value) {
    dateRangeInput.value.value = `${start.format("DD/MM/YYYY")} - ${end.format(
      "DD/MM/YYYY"
    )}`;
  }

  reload();
}

function setSort(mode) {
  if (mode === "last_7_days") {
    const end = moment();
    const start = moment().subtract(6, "days");
    applyDateRange(start, end);
    sortMode.value = "last_7_days";
    return;
  }

  if (mode === "last_month") {
    const end = moment().subtract(1, "month").endOf("month");
    const start = moment().subtract(1, "month").startOf("month");
    applyDateRange(start, end);
    sortMode.value = "last_month";
    return;
  }

  sortMode.value = mode;
}

function applyDateRange(start, end) {
  expenseStore.filters.date_from = start.format("YYYY-MM-DD");
  expenseStore.filters.date_to = end.format("YYYY-MM-DD");
  expenseStore.filters.page = 1;

  if (dateRangeInput.value) {
    dateRangeInput.value.value = `${start.format("DD/MM/YYYY")} - ${end.format(
      "DD/MM/YYYY"
    )}`;
  }

  reload();
}

function initDatePicker(start, end) {
  if (!dateRangeInput.value) return;

  new DateRangePicker(
    dateRangeInput.value,
    {
      startDate: start,
      endDate: end,
      ranges: {
        Today: [moment(), moment()],
        Yesterday: [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [
          moment().subtract(1, "month").startOf("month"),
          moment().subtract(1, "month").endOf("month"),
        ],
      },
    },
    (startDate, endDate) => {
      applyDateRange(startDate, endDate);
    }
  );

  dateRangeInput.value.value = `${start.format("DD/MM/YYYY")} - ${end.format(
    "DD/MM/YYYY"
  )}`;
}

function calcPercent(curr, prev) {
  const c = Number(curr || 0);
  const p = Number(prev || 0);
  if (p <= 0) return null;
  return ((c - p) / p) * 100;
}

async function updateComparisons() {
  try {
    const fromStr = expenseStore.filters.date_from;
    const toStr = expenseStore.filters.date_to;

    if (!fromStr || !toStr) {
      changePercents.value = {
        total: null,
        approved: null,
        pending: null,
        paid: null,
      };
      return;
    }

    const from = moment(fromStr, "YYYY-MM-DD");
    const to = moment(toStr, "YYYY-MM-DD");
    const days = Math.max(1, to.diff(from, "days") + 1);

    const prevTo = from.clone().subtract(1, "day");
    const prevFrom = from.clone().subtract(days, "day");

    const prevStats = await expenseStore.statsForRange({
      date_from: prevFrom.format("YYYY-MM-DD"),
      date_to: prevTo.format("YYYY-MM-DD"),
    });

    changePercents.value = {
      total: calcPercent(stats.value.total, prevStats.total),
      approved: calcPercent(stats.value.approved, prevStats.approved),
      pending: calcPercent(stats.value.pending, prevStats.pending),
      paid: calcPercent(stats.value.paid, prevStats.paid),
    };
  } catch (e) {
    console.error("Failed to compute comparison stats", e);
  }
}

async function reload(extra = {}) {
  if (!extra.per_page && expenseStore.filters.per_page > 1000) {
    expenseStore.filters.per_page = 1000;
  }

  await Promise.all([
    expenseStore.fetchAll(extra),
    expenseStore.fetchStats(extra),
  ]);

  await updateComparisons();
}

/** ---- Export helpers ---- */

function toCsvValue(val) {
  const s = String(val ?? "");
  if (s.includes(",") || s.includes('"') || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function exportExcel() {
  const rows = sortedData.value;
  if (!rows.length) return;

  const header = [
    "Expense Name",
    "Date",
    "Category",
    "Payment Method",
    "Amount",
    "Currency",
    "Status",
  ];

  const lines = [header.map(toCsvValue).join(",")];

  rows.forEach((r) => {
    const row = [
      r.name,
      r.expense_date || "",
      r.category || "",
      r.payment_method || "",
      r.amount,
      r.currency || "",
      r.status || "",
    ];
    lines.push(row.map(toCsvValue).join(","));
  });

  const blob = new Blob([lines.join("\n")], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute(
    "download",
    `expenses-report-${new Date().toISOString().slice(0, 10)}.csv`
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function exportPdf() {
  try {
    const blob = await expenseStore.exportPdf(); // uses current filters

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `expenses-report-${new Date()
      .toISOString()
      .slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (e) {
    // error already toasted by store
    console.error("Export PDF failed", e);
  }
}

/** ---- Lifecycle ---- */

onMounted(async () => {
  // Load a reasonable amount of data for reporting
  expenseStore.filters.per_page = 100;

  // Default: Last 7 days
  const start = moment().subtract(6, "days");
  const end = moment();
  sortMode.value = "last_7_days";

  initDatePicker(start, end);
  applyDateRange(start, end);
});
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <!-- Page Wrapper -->
  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div
        class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3"
      >
        <breadcrumb :title="title" :text="text" :text1="text1" />
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions"
          >
          <!-- Export -->
          <div class="mb-2">
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportPdf"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="exportExcel"
                    v-can="'Reports.Export'"
                  >
                    <i class="ti ti-file-type-xls me-1"></i>Export as Excel
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <!-- Collapse header -->
          <div class="head-icons ms-2">
            <a
              href="javascript:void(0);"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              data-bs-original-title="Collapse"
              id="collapse-header"
              @click="toggleHeader"
            >
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>
      <!-- /Breadcrumb -->

      <div class="row">
        <!-- Summary Cards -->
        <div class="col-xl-6 d-flex">
          <div class="row flex-fill dq-stat-grid">
            <!-- Total Expense -->
            <div class="col-md-6 d-flex">
              <div class="card flex-fill position-relative dq-stat-card">
                <span class="position-absolute start-0 bottom-0">
                  <img
                    src="@/assets/img/reports-img/total-expense.svg"
                    alt="img"
                    class="img-fluid"
                  />
                </span>
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between mb-2"
                  >
                    <div>
                      <span class="fs-14 fw-normal text-truncate mb-1"
                        >Total Expense</span
                      >
                      <h5>KES {{ fmtAmount(stats.total) }}</h5>
                    </div>
                    <a
                      href="javascript:void(0);"
                      class="avatar avatar-md avatar-rounded bg-transparent-primary border border-primary"
                    >
                      <span class="text-primary"
                        ><i class="ti ti-brand-shopee"></i
                      ></span>
                    </a>
                  </div>
                  <!-- <p
                    class="fs-12 fw-normal d-flex align-items-center text-truncate"
                  >
                    <span
                      class="fs-12 d-flex align-items-center me-1"
                      :class="[
                        changePercents.total === null ||
                        changePercents.total >= 0
                          ? 'text-success'
                          : 'text-danger',
                      ]"
                    >
                      <i
                        class="ti me-1"
                        :class="[
                          changePercents.total === null ||
                          changePercents.total >= 0
                            ? 'ti-arrow-wave-right-up'
                            : 'ti-arrow-wave-right-down',
                        ]"
                      ></i>
                      {{
                        changePercents.total === null
                          ? "0.00%"
                          : (changePercents.total > 0 ? "+" : "") +
                            changePercents.total.toFixed(2) +
                            "%"
                      }}
                    </span>
                    from previous period
                  </p> -->
                </div>
              </div>
            </div>

            <!-- Approved Expense -->
            <div class="col-md-6 d-flex">
              <div class="card flex-fill position-relative dq-stat-card">
                <span class="position-absolute start-0 bottom-0">
                  <img
                    src="@/assets/img/reports-img/approved-expense.svg"
                    alt="img"
                    class="img-fluid"
                  />
                </span>
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between mb-2"
                  >
                    <div>
                      <span class="fs-14 fw-normal text-truncate mb-1"
                        >Approved Expense</span
                      >
                      <h5>KES {{ fmtAmount(stats.approved) }}</h5>
                    </div>
                    <a
                      href="javascript:void(0);"
                      class="avatar avatar-md avatar-rounded bg-transparent-success border border-success"
                    >
                      <span class="text-success"
                        ><i class="ti ti-brand-shopee"></i
                      ></span>
                    </a>
                  </div>
                  <!-- <p
                    class="fs-12 fw-normal d-flex align-items-center text-truncate"
                  >
                    <span
                      class="fs-12 d-flex align-items-center me-1"
                      :class="[
                        changePercents.approved === null ||
                        changePercents.approved >= 0
                          ? 'text-success'
                          : 'text-danger',
                      ]"
                    >
                      <i
                        class="ti me-1"
                        :class="[
                          changePercents.approved === null ||
                          changePercents.approved >= 0
                            ? 'ti-arrow-wave-right-up'
                            : 'ti-arrow-wave-right-down',
                        ]"
                      ></i>
                      {{
                        changePercents.approved === null
                          ? "0.00%"
                          : (changePercents.approved > 0 ? "+" : "") +
                            changePercents.approved.toFixed(2) +
                            "%"
                      }}
                    </span>
                    from previous period
                  </p> -->
                </div>
              </div>
            </div>

            <!-- Pending Expense -->
            <div class="col-md-6 d-flex">
              <div class="card flex-fill position-relative dq-stat-card">
                <span class="position-absolute start-0 bottom-0">
                  <img
                    src="@/assets/img/reports-img/pending-expense.svg"
                    alt="img"
                    class="img-fluid"
                  />
                </span>
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between mb-2"
                  >
                    <div>
                      <span class="fs-14 fw-normal text-truncate mb-1"
                        >Pending Expense</span
                      >
                      <h5>KES {{ fmtAmount(stats.pending) }}</h5>
                    </div>
                    <a
                      href="javascript:void(0);"
                      class="avatar avatar-md avatar-rounded bg-transparent-skyblue border border-skyblue"
                    >
                      <span class="text-skyblue"
                        ><i class="ti ti-brand-shopee"></i
                      ></span>
                    </a>
                  </div>
                  <!-- <p
                    class="fs-12 fw-normal d-flex align-items-center text-truncate"
                  >
                    <span
                      class="fs-12 d-flex align-items-center me-1"
                      :class="[
                        changePercents.pending === null ||
                        changePercents.pending >= 0
                          ? 'text-success'
                          : 'text-danger',
                      ]"
                    >
                      <i
                        class="ti me-1"
                        :class="[
                          changePercents.pending === null ||
                          changePercents.pending >= 0
                            ? 'ti-arrow-wave-right-up'
                            : 'ti-arrow-wave-right-down',
                        ]"
                      ></i>
                      {{
                        changePercents.pending === null
                          ? "0.00%"
                          : (changePercents.pending > 0 ? "+" : "") +
                            changePercents.pending.toFixed(2) +
                            "%"
                      }}
                    </span>
                    from previous period
                  </p> -->
                </div>
              </div>
            </div>

            <!-- Paid Expense -->
            <div class="col-md-6 d-flex">
              <div class="card flex-fill position-relative dq-stat-card">
                <span class="position-absolute start-0 bottom-0">
                  <img
                    src="@/assets/img/reports-img/reject-expense.svg"
                    alt="img"
                    class="img-fluid"
                  />
                </span>
                <div class="card-body">
                  <div
                    class="d-flex align-items-center justify-content-between mb-2"
                  >
                    <div>
                      <span class="fs-14 fw-normal text-truncate mb-1"
                        >Paid Expense</span
                      >
                      <h5>KES {{ fmtAmount(stats.paid) }}</h5>
                    </div>
                    <a
                      href="javascript:void(0);"
                      class="avatar avatar-md avatar-rounded bg-transparent-danger border border-danger"
                    >
                      <span class="text-danger"
                        ><i class="ti ti-brand-shopee"></i
                      ></span>
                    </a>
                  </div>
                  <!-- <p
                    class="fs-12 fw-normal d-flex align-items-center text-truncate"
                  >
                    <span
                      class="fs-12 d-flex align-items-center me-1"
                      :class="[
                        changePercents.paid === null || changePercents.paid >= 0
                          ? 'text-success'
                          : 'text-danger',
                      ]"
                    >
                      <i
                        class="ti me-1"
                        :class="[
                          changePercents.paid === null ||
                          changePercents.paid >= 0
                            ? 'ti-arrow-wave-right-up'
                            : 'ti-arrow-wave-right-down',
                        ]"
                      ></i>
                      {{
                        changePercents.paid === null
                          ? "0.00%"
                          : (changePercents.paid > 0 ? "+" : "") +
                            changePercents.paid.toFixed(2) +
                            "%"
                      }}
                    </span>
                    from previous period
                  </p> -->
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- /Summary Cards -->

        <!-- Expense Chart -->
        <div class="col-xl-6 d-flex">
          <div class="card flex-fill">
            <div class="card-header border-0 pb-0">
              <div
                class="d-flex flex-wrap justify-content-between align-items-center"
              >
                <div class="d-flex align-items-center">
                  <span class="me-2"
                    ><i class="ti ti-chart-area-line text-danger"></i
                  ></span>
                  <h5>Expense</h5>
                </div>
                <div class="dropdown">
                  <a
                    href="javascript:void(0);"
                    class="dropdown-toggle btn btn-sm fs-12 btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {{ selectedYear }}
                  </a>
                  <ul class="dropdown-menu dropdown-menu-end p-2">
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="setYear(moment().year())"
                      >
                        {{ moment().year() }}
                      </a>
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="setYear(moment().year() - 1)"
                      >
                        {{ moment().year() - 1 }}
                      </a>
                    </li>
                    <li>
                      <a
                        href="javascript:void(0);"
                        class="dropdown-item rounded-1"
                        @click.prevent="setYear(moment().year() - 2)"
                      >
                        {{ moment().year() - 2 }}
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div class="card-body py-0">
              <div id="expense-analysis">
                <apexchart
                  type="line"
                  height="130"
                  :options="expenseChartOptions"
                  :series="expenseChartSeries"
                ></apexchart>
              </div>
            </div>
          </div>
        </div>
        <!-- /Expense Chart -->
      </div>

      <!-- List + filters -->
      <div class="card">
        <div
          class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3"
        >
          <h5>Expense List</h5>
          <div
            class="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3 dq-list-toolbar"
          >
            <!-- Date range -->
            <div class="me-3">
              <div class="input-icon-end position-relative">
                <input
                  type="text"
                  class="form-control date-range bookingrange"
                  ref="dateRangeInput"
                  placeholder="dd/mm/yyyy - dd/mm/yyyy"
                  readonly
                />
                <span class="input-icon-addon">
                  <i class="ti ti-chevron-down"></i>
                </span>
              </div>
            </div>

            <!-- Amount filter -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ amountLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li
                  v-for="r in amountRanges"
                  :key="r.label"
                  @click.prevent="setAmountRange(r)"
                >
                  <a href="javascript:void(0);" class="dropdown-item rounded-1">
                    {{ r.label }}
                  </a>
                </li>
              </ul>
            </div>

            <!-- Payment type -->
            <div class="dropdown me-3">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                {{ paymentLabel === "All" ? "Payment Type" : paymentLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li
                  v-for="m in paymentMethods"
                  :key="m"
                  @click.prevent="setPaymentMethod(m)"
                >
                  <a href="javascript:void(0);" class="dropdown-item rounded-1">
                    {{ m }}
                  </a>
                </li>
              </ul>
            </div>

            <!-- Sort -->
            <div class="dropdown">
              <a
                href="javascript:void(0);"
                class="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                data-bs-toggle="dropdown"
              >
                Sort By : {{ sortLabel }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('recent')"
                    >Recently Added</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('amount_asc')"
                    >Amount (Low → High)</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('amount_desc')"
                    >Amount (High → Low)</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('last_month')"
                    >Last Month</a
                  >
                </li>
                <li>
                  <a
                    href="javascript:void(0);"
                    class="dropdown-item rounded-1"
                    @click.prevent="setSort('last_7_days')"
                    >Last 7 Days</a
                  >
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <expenses-report-table
              :items="sortedData"
              :loading="loading"
            ></expenses-report-table>
          </div>
        </div>
      </div>
    </div>

    <div
      class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3"
    >
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by
        <a href="javascript:void(0);" class="text-primary"
          >DoneQuick Technological World</a
        >
      </p>
    </div>
  </div>
  <!-- /Page Wrapper -->
</template>

<style scoped>
@media (max-width: 767.98px) {
  .dq-list-toolbar {
    align-items: stretch !important;
  }

  .dq-list-toolbar > * {
    width: 100%;
    margin-right: 0 !important;
  }

  .dq-list-toolbar .dropdown,
  .dq-list-toolbar .dropdown-toggle,
  .dq-list-toolbar .form-control,
  .dq-list-toolbar .input-icon-end {
    width: 100%;
  }

  .dq-stat-card .card-body {
    padding: 1rem;
  }

  .custom-datatable-filter.table-responsive {
    margin-left: -1rem;
    margin-right: -1rem;
    padding-left: 1rem;
    padding-right: 1rem;
    width: calc(100% + 2rem);
  }
}
</style>


